if (typeof ScrollingText !== "function") {
  class ScrollingText extends HTMLElement {
    constructor() {
      super();

      this._animations = [];
      this._generatedClones = [];
      this._initFrame = 0;
      this._rateEaseFrame = 0;
      this._playbackRate = 1;
      this._isIntersecting = true;
      this._hovered = false;
      this._windowInFocus = true;
      this._running = false;

      this._onMouseOver = this._onMouseOver.bind(this);
      this._onMouseOut = this._onMouseOut.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onBlur = this._onBlur.bind(this);
      this._onFocus = this._onFocus.bind(this);
    }

    connectedCallback() {
      if (this._initFrame) return;

      this._initFrame = requestAnimationFrame(() => {
        this._initFrame = 0;
        if (this.isConnected) this.init();
      });
    }

    disconnectedCallback() {
      this._teardown();
    }

    init() {
      this._teardownRuntime();

      this._base = this.children[0];
      if (!this._base) return;

      this._reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      if (this._reduceMotion.matches) {
        this.setAttribute("data-motion-reduced", "");
        return;
      }

      this.removeAttribute("data-motion-reduced");
      this._direction = this.dataset.scrollingDirection === "rtl" ? "rtl" : "ltr";
      this._pauseOnHover = this.dataset.pauseOnHover === "true";
      this._hoverEaseDuration = this.dataset.pauseOnHoverEase === "true" ? 1 : 0;
      this._hovered = false;
      this._windowInFocus = true;
      this._isIntersecting = true;
      this._playbackRate = 1;

      this._base.style.position = "relative";
      this._template = this._base.cloneNode(true);

      this._readSpeed();
      this._readSizes();
      this._setupChildren();
      this._setupHoverPause();
      this._setupIntersectionObserver();
      this._setupResizeObserver();
      this._createAnimations(0, 1);
    }

    _readSpeed() {
      const configuredSpeed = Number.parseFloat(this.dataset.scrollingSpeed);
      const responsiveSpeed = window.innerWidth > 768
        ? configuredSpeed
        : configuredSpeed / 1.5;

      this._speed = Number.isFinite(responsiveSpeed) && responsiveSpeed > 0
        ? responsiveSpeed
        : 0;
    }

    _readSizes() {
      const rectBox = this.getBoundingClientRect();
      const rectChild = this._base.getBoundingClientRect();

      this._boxWidth = rectBox.width;
      this._itemWidth = rectChild.width;
    }

    _neededChildren() {
      if (!Number.isFinite(this._itemWidth) || this._itemWidth <= 0) return 1;
      return Math.max(1, Math.ceil(this._boxWidth / this._itemWidth) + 1);
    }

    _setupChildren() {
      this._removeGeneratedClones();

      const needed = this._neededChildren();
      const current = this.children.length;

      for (let index = current; index < needed; index += 1) {
        const clone = this._template.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.removeAttribute("id");
        clone.querySelectorAll("[id]").forEach((element) => element.removeAttribute("id"));
        this.appendChild(clone);
        this._generatedClones.push(clone);
      }
    }

    _removeGeneratedClones() {
      for (const clone of this._generatedClones) {
        if (clone.parentNode === this) clone.remove();
      }
      this._generatedClones = [];
    }

    _createAnimations(progress = 0, playbackRate = 1) {
      this._cancelAnimations();

      if (!this._speed || !this._itemWidth || typeof this._base.animate !== "function") return;

      this._duration = (this._itemWidth / this._speed) * 1000;
      this._travelDistance = this._direction === "rtl" ? this._itemWidth : -this._itemWidth;
      const normalizedProgress = Number.isFinite(progress)
        ? ((progress % 1) + 1) % 1
        : 0;

      for (const child of this.children) {
        const animation = child.animate(
          [
            { transform: "translate3d(0, 0, 0)" },
            { transform: `translate3d(${this._travelDistance}px, 0, 0)` },
          ],
          {
            duration: this._duration,
            iterations: Infinity,
            easing: "linear",
          },
        );

        animation.currentTime = normalizedProgress * this._duration;
        this._animations.push(animation);
      }

      this._setPlaybackRate(playbackRate);
      this._syncPlaybackState();
    }

    _cancelAnimations() {
      for (const animation of this._animations) animation.cancel();
      this._animations = [];
      this._running = false;
    }

    _normalizedProgress() {
      const currentTime = this._animations[0]?.currentTime;
      if (!this._duration || !Number.isFinite(currentTime)) return 0;
      return ((currentTime / this._duration) % 1 + 1) % 1;
    }

    _setPlaybackRate(rate) {
      const safeRate = Number.isFinite(rate) ? Math.max(0, Math.min(1, rate)) : 1;
      this._playbackRate = safeRate;

      for (const animation of this._animations) {
        if (typeof animation.updatePlaybackRate === "function") {
          animation.updatePlaybackRate(safeRate);
        } else {
          animation.playbackRate = safeRate;
        }
      }
    }

    _playAnimations() {
      if (!this._animations.length || this._playbackRate <= 0) {
        this._pauseAnimations();
        return;
      }

      for (const animation of this._animations) animation.play();
      this._running = true;
    }

    _pauseAnimations() {
      for (const animation of this._animations) animation.pause();
      this._running = false;
    }

    _syncPlaybackState() {
      if (!this._isIntersecting) {
        this._pauseAnimations();
        return;
      }

      if (this._hovered) {
        if (this._hoverEaseDuration > 0 && this._playbackRate > 0) {
          this._easePlaybackRate(0);
        } else {
          this._pauseAnimations();
        }
        return;
      }

      if (this._hoverEaseDuration > 0 && this._playbackRate < 1) {
        this._easePlaybackRate(1);
      } else {
        this._setPlaybackRate(1);
        this._playAnimations();
      }
    }

    _easePlaybackRate(targetRate) {
      this._cancelRateEase();

      if (!this._isIntersecting || !this._animations.length) {
        this._pauseAnimations();
        return;
      }

      const startRate = this._playbackRate;
      const duration = this._hoverEaseDuration * 1000;
      if (!duration || startRate === targetRate) {
        this._setPlaybackRate(targetRate);
        if (targetRate === 0) this._pauseAnimations();
        else this._playAnimations();
        return;
      }

      if (targetRate > 0) {
        this._setPlaybackRate(Math.max(startRate, 0.001));
        this._playAnimations();
      }

      let startTime;
      const ease = (timestamp) => {
        if (!this._isIntersecting || !this.isConnected) {
          this._rateEaseFrame = 0;
          this._pauseAnimations();
          return;
        }

        if (startTime === undefined) startTime = timestamp;
        const elapsed = Math.min(1, (timestamp - startTime) / duration);
        this._setPlaybackRate(startRate + (targetRate - startRate) * elapsed);

        if (elapsed < 1) {
          this._rateEaseFrame = requestAnimationFrame(ease);
          return;
        }

        this._rateEaseFrame = 0;
        if (targetRate === 0) this._pauseAnimations();
        else this._playAnimations();
      };

      this._rateEaseFrame = requestAnimationFrame(ease);
    }

    _cancelRateEase() {
      if (!this._rateEaseFrame) return;
      cancelAnimationFrame(this._rateEaseFrame);
      this._rateEaseFrame = 0;
    }

    _setupHoverPause() {
      if (!this._pauseOnHover) return;

      window.addEventListener("blur", this._onBlur);
      window.addEventListener("focus", this._onFocus);
      this.addEventListener("mouseover", this._onMouseOver);
      this.addEventListener("mouseout", this._onMouseOut);
    }

    _onBlur() {
      this._windowInFocus = false;
    }

    _onFocus() {
      this._windowInFocus = true;
    }

    _onMouseOver() {
      if (!this._windowInFocus || this._hovered) return;
      this._hovered = true;

      if (this._hoverEaseDuration > 0) this._easePlaybackRate(0);
      else this._pauseAnimations();
    }

    _onMouseOut() {
      if (!this._windowInFocus || !this._hovered) return;
      this._hovered = false;

      if (this._hoverEaseDuration > 0) this._easePlaybackRate(1);
      else if (this._isIntersecting) this._playAnimations();
    }

    _setupIntersectionObserver() {
      this._intersectionObserver = new IntersectionObserver((entries) => {
        this._isIntersecting = Boolean(entries[0]?.isIntersecting);
        this._cancelRateEase();
        this._syncPlaybackState();
      });
      this._intersectionObserver.observe(this);
    }

    _setupResizeObserver() {
      this._resizeObserver = new ResizeObserver(this._onResize);
      this._resizeObserver.observe(this);
    }

    _onResize() {
      if (!this._base || this._reduceMotion?.matches) return;

      const progress = this._normalizedProgress();
      const playbackRate = this._playbackRate;
      this._cancelRateEase();
      this._cancelAnimations();
      this._readSpeed();
      this._readSizes();
      this._setupChildren();
      this._createAnimations(progress, playbackRate);
    }

    start() {
      this._isIntersecting = true;
      this._syncPlaybackState();
    }

    stop() {
      this._cancelRateEase();
      this._pauseAnimations();
    }

    _teardownRuntime() {
      this._cancelRateEase();
      this._cancelAnimations();

      if (this._intersectionObserver) this._intersectionObserver.disconnect();
      if (this._resizeObserver) this._resizeObserver.disconnect();
      this._intersectionObserver = null;
      this._resizeObserver = null;

      window.removeEventListener("blur", this._onBlur);
      window.removeEventListener("focus", this._onFocus);
      this.removeEventListener("mouseover", this._onMouseOver);
      this.removeEventListener("mouseout", this._onMouseOut);

      this._removeGeneratedClones();
    }

    _teardown() {
      if (this._initFrame) {
        cancelAnimationFrame(this._initFrame);
        this._initFrame = 0;
      }

      this._teardownRuntime();
    }
  }

  if (typeof customElements.get("scrolling-text") === "undefined") {
    customElements.define("scrolling-text", ScrollingText);
  }
}
